import React, { useEffect, useState } from 'react';
import NavigationStepper, {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../Components/Stepper/NavigationStepper';
import { PrescriptionType } from '../../../Enums/PrescriptionType.enum';
import ScrollContainer from '../../Base/Components/ScrollContainer';
import { FormValues } from '../../MagicForm/Interfaces/FormValues';
import EPrescriptionPrescriptionInformation, {
	EPrescriptionIssuePrescriptionFormData,
} from './EPrescriptionIssuePrescription';
import { EPrescriptionHVLPMedicament } from './EPrescriptionMedicamentsHVLPForm';
import { EPrescriptionIPLPMedicament } from './EPrescriptionMedicamentsIPLPForm';
import EPrescriptionMedicamentsWrapper, {
	EPrescriptionMedicament,
} from './EPrescriptionMedicamentsWrapper';
import EPrescriptionPatientInformation, {
	EPrescriptionPatientInformationFormData,
} from './EPrescriptionPatientInformation';
import SearchMedicamentRecordAuthorization, {
	SearchMedicamentRecordAuthorizationSource,
} from '../../../Components/eRecept/SearchMedicamentRecord/SearchMedicamentRecordAuthorization';
import { useEffectAsync } from '../../../helpers';
import { checkSUKLCredentialsAvailability } from '../Services/SUKLCredentials.service';
import usePrescriptionHelper from '../Hooks/usePrescriptionHelper';
import EPrescriptionWizardPatientData from './EPrescriptionWizard/PatientData/EPrescriptionWizardPatientData';
import { EPrescriptionWizardPatientDataFormValues } from './EPrescriptionWizard/PatientData/EPrescriptionWizardPatientDataForm';

interface EPrescriptionWrapperProps {
	patientId: string;
	onPrescriptionCreated: (receiptId: string, medicaments: string) => void;
	defaultValues?: any;
	setIsLoading: (isLoading: boolean) => void;
	customOnSubmit?: (values: any) => void;
	createDekurz?: boolean;
}

interface MedicamentsValues {
	hvlp?: EPrescriptionHVLPMedicament[];
	iplp?: EPrescriptionIPLPMedicament[];
}

export interface EPrescriptionWizardData {
	patientInformation?: EPrescriptionWizardPatientDataFormValues;
	medicaments?: MedicamentsValues;
	issuePrescription?: EPrescriptionIssuePrescriptionFormData;

	[key: string]: any;
}

const EPrescriptionWrapper: React.FC<EPrescriptionWrapperProps> = ({
	patientId,
	onPrescriptionCreated,
	defaultValues,
	setIsLoading,
	customOnSubmit,
	createDekurz,
}) => {
	const [formValues, setFormValues] = useState<EPrescriptionWizardData>({});
	const [steps, setSteps] = useState<NavigationStepperItemType[]>([]);
	const [initialStep, setInitialStep] = useState('informationAboutPatient');
	const { prescriptionDefaultValues } = usePrescriptionHelper();

	useEffect(() => {
		if (defaultValues?.formData?.default?.medicaments) {
			setFormValues({
				medicaments: defaultValues.formData.default.medicaments,
			});
		}
	}, [defaultValues]);

	const handlePatientInformation = (
		params: NavigationStepperComponentParams,
		values: EPrescriptionWizardPatientDataFormValues,
	) => {
		setFormValues(prevState => {
			return {
				...prevState,
				patientInformation: values,
				issuePrescription: {
					...(prevState?.issuePrescription || {}),
					notificationPhoneNumber: `${values.phoneNumber}`,
					notificationEmail: `${values.electronicMail}`,
					...prescriptionDefaultValues.issuePrescription,
				},
			};
		});
		params.setActiveStepPath('medicaments');
	};

	const handleMedicaments = (values: EPrescriptionMedicament[]) => {
		setFormValues(prevState => {
			return {
				...prevState,
				medicaments: {
					hvlp: (values as unknown as EPrescriptionHVLPMedicament[])?.filter(
						({ formType }) => formType === PrescriptionType.HVLP,
					),
					iplp: (values as unknown as EPrescriptionIPLPMedicament[])?.filter(
						({ formType }) => formType === PrescriptionType.IPLP,
					),
				},
			};
		});
	};

	const handleAfterMedicaments = (params: NavigationStepperComponentParams) => {
		params.setActiveStepPath('issuePrescription');
	};

	const getMedicamentsNames = (): string => {
		const names = [
			...(formValues.medicaments?.hvlp?.map(m => m.item.name) ?? []),
			...(formValues.medicaments?.iplp?.map(m => m.name) ?? []),
		];
		return names.join(', ');
	};

	useEffectAsync(async () => {
		setIsLoading(true);
		const tempSteps: NavigationStepperItemType[] = [];

		const isMissingSUKLCredentials = await checkSUKLCredentialsAvailability();

		if (isMissingSUKLCredentials) {
			setInitialStep('authorization');
			tempSteps.push({
				name: 'base.verifyOfAccess',
				key: 'authorization',
				component: (params: NavigationStepperComponentParams) => (
					<SearchMedicamentRecordAuthorization
						setIsLoading={params.setIsLoading}
						source={SearchMedicamentRecordAuthorizationSource.ERECEPT}
						onSuccess={() =>
							params.setActiveStepPath('informationAboutPatient')
						}
					/>
				),
			});
		}

		tempSteps.push(
			{
				name: 'base.patientDetails',
				key: 'informationAboutPatient',
				component: (params: NavigationStepperComponentParams) => (
					<EPrescriptionWizardPatientData
						patientId={patientId}
						onSubmit={values => handlePatientInformation(params, values)}
						wizardData={formValues}
					/>
				),
			},
			{
				name: 'base.inputMeds',
				key: 'medicaments',
				component: (params: NavigationStepperComponentParams) => (
					<EPrescriptionMedicamentsWrapper
						patientId={patientId}
						onMedicamentsUpdate={values => handleMedicaments(values)}
						onSuccess={() => handleAfterMedicaments(params)}
						defaultValues={formValues.medicaments}
						formValues={formValues}
					/>
				),
			},
			{
				name: 'base.issuePrescription',
				key: 'issuePrescription',
				component: (params: NavigationStepperComponentParams) => (
					<EPrescriptionPrescriptionInformation
						patientId={patientId}
						formValues={formValues}
						defaultValues={formValues.issuePrescription}
						onLoading={(isLoading: boolean) => params.setIsLoading(isLoading)}
						onPrescriptionCreated={(receiptId: string) =>
							onPrescriptionCreated(receiptId, getMedicamentsNames())
						}
						customOnSubmit={customOnSubmit}
						createDekurz={createDekurz}
					/>
				),
			},
		);

		setSteps(tempSteps);
		setIsLoading(false);
	}, [formValues, prescriptionDefaultValues]);

	return (
		<ScrollContainer>
			<NavigationStepper
				steps={steps}
				initialStepPath={initialStep}
				canSkip={false}
			/>
		</ScrollContainer>
	);
};

export default EPrescriptionWrapper;
